import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import './BusinessAnalyticsPage.css';
import OutletBadge from './OutletBadge';
// If you have a date picker component, import it, else use input type="date"

const COLORS = ['#0088FE', '#FF8042', '#00C49F', '#FFBB28', '#FF4444', '#8884d8', '#82ca9d'];
const RATING_COLORS = ['#228B22', '#90ee90', '#ffb347']; // dark green, light green, orange

function getYesterday() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function getToday() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function getThisWeek() {
  const now = new Date();
  const day = now.getDay() || 7;
  const start = new Date(now);
  start.setDate(now.getDate() - day + 1);
  return [start.toISOString().slice(0, 10), now.toISOString().slice(0, 10)];
}

function getLastWeek() {
  const now = new Date();
  const day = now.getDay() || 7;
  const end = new Date(now);
  end.setDate(now.getDate() - day);
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}

function getThisMonth() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return [start.toISOString().slice(0, 10), now.toISOString().slice(0, 10)];
}

function getLastMonth() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
}

function formatDateDM(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' }).toLowerCase()}`;
}

function formatHour12(hourStr) {
  if (!hourStr) return '';
  let [h] = hourStr.split(':');
  h = parseInt(h, 10);
  if (isNaN(h)) return hourStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12} ${ampm}`;
}

const PRESETS = [
  { label: 'Today', get: () => [getToday(), getToday()] },
  { label: 'Yesterday', get: () => [getYesterday(), getYesterday()] },
  { label: 'This Week', get: getThisWeek },
  { label: 'Last Week', get: getLastWeek },
  { label: 'This Month', get: getThisMonth },
  { label: 'Last Month', get: getLastMonth },
  { label: 'Custom', get: null },
];

const BusinessAnalyticsPage = ({ token, business_id, businessName, onBack }) => {
  const [preset, setPreset] = useState('Yesterday');
  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getYesterday());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${process.env.REACT_APP_API_URL}/business/business_analytics?auth_token=${token}&start_date=${startDate}&end_date=${endDate}`;
      const response = await fetch(url);
      const result = await response.json();
      if (!result.success) throw new Error(result.message || 'Failed to fetch analytics');
      setData(result.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Pie chart data
  const pieEntries = [
    { name: 'Entries', value: data?.totals?.total_entries || 0 },
    { name: 'Deleted Entries', value: data?.totals?.deleted_entries || 0 },
  ];
  const piePeople = [
    { name: 'People Entered', value: data?.totals?.total_people || 0 },
    { name: 'People Deleted', value: data?.totals?.deleted_people || 0 },
  ];

  // Ratings pie chart data
  const ratingStats = data?.ratings || { '5_star': 0, '4_star': 0, '3_or_less': 0 };
  const ratingPie = [
    { name: '5 Star', value: ratingStats['5_star'] || 0 },
    { name: '4 Star', value: ratingStats['4_star'] || 0 },
    { name: '3 Star or less', value: ratingStats['3_or_less'] || 0 },
  ];
  const totalRatings = ratingPie.reduce((sum, r) => sum + r.value, 0);
  const avgRating = totalRatings === 0 ? 0 : (
    ( (ratingStats['5_star'] || 0) * 5 + (ratingStats['4_star'] || 0) * 4 + (ratingStats['3_or_less'] || 0) * 2 ) / totalRatings
  ).toFixed(1);

  // Daily line/bar data
  const dailyData = (data?.graph_overview || []).map(d => ({
    date: formatDateDM(d.date),
    entries: d.entries,
    people: d.people,
  }));

  // Weekly line data
  const weeklyData = (data?.weekly_data || []).map(d => ({
    week: `${formatDateDM(d.week_start)} - ${formatDateDM(d.week_end)}`,
    entries: d.entries,
    people: d.people,
  }));

  // Hourly data
  const hourlyData = (data?.hourly_data || []).map(d => ({
    hour: formatHour12(d.hour),
    entries: d.entries,
    people: d.people,
  }));

  // Waiting number vs avg time
  const waitingData = (data?.waiting?.waiting_numbers || []).map(d => ({
    waiting_number: d.waiting_number,
    avg_wait: d.avg_wait,
  }));

  // Deletion reasons table
  const deletionReasons = data?.deletion_reasons || [];

  // Bar size for single date
  const barSize = startDate === endDate ? 40 : undefined;
  const weeklyBarSize = 40;

  // Date range picker UI
  const renderDatePicker = () => (
    <div className="bap-date-picker">
      <select
        className="bap-preset-dropdown"
        value={preset}
        onChange={e => {
          const selected = e.target.value;
          setPreset(selected);
          const presetObj = PRESETS.find(p => p.label === selected);
          if (presetObj && presetObj.get) {
            const [s, e] = presetObj.get();
            setStartDate(s);
            setEndDate(e);
          }
        }}
      >
        {PRESETS.map(presetObj => (
          <option key={presetObj.label} value={presetObj.label}>{presetObj.label}</option>
        ))}
      </select>
      {preset === 'Custom' && (
        <span style={{ marginLeft: 16 }}>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          <span style={{ margin: '0 8px' }}>to</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </span>
      )}
    </div>
  );

  return (
    <div className="bap-root">
      <div className="bap-header-bar">
        <div className="bap-title-outlet-group">
          <h2 className="bap-title" style={{ margin: 0 }}>Business Analytics</h2>
          <OutletBadge businessName={businessName} businessId={business_id} pageTitle="Reports & Insights" />
        </div>
        {onBack && <button onClick={onBack} className="bap-back-btn">Back</button>}
      </div>
      <div style={{ marginBottom: 16 }}>
        {renderDatePicker()}
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {data && (
        <>
          <div className={isMobile ? 'bap-stack' : 'bap-row'}>
            <div className="bap-graph-box">
              <h4>Entries vs Deleted</h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieEntries} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {pieEntries.map((entry, idx) => <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bap-graph-box">
              <h4>People Entered vs Deleted</h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={piePeople} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {piePeople.map((entry, idx) => <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bap-graph-box">
              <h4>Ratings
                <span style={{ fontWeight: 'normal', fontSize: 15, marginLeft: 8 }}>
                  Avg: <span style={{ color: '#228B22', fontWeight: 600 }}>{avgRating}</span>
                </span>
              </h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={ratingPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {ratingPie.map((entry, idx) => <Cell key={entry.name} fill={RATING_COLORS[idx % RATING_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={isMobile ? 'bap-stack' : 'bap-row'}>
            <div className="bap-graph-box">
              <h4>Daily Entries & People</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barSize={barSize}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" orientation="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="people" fill="#8884d8" name="People" />
                  <Line yAxisId="right" type="monotone" dataKey="entries" stroke="#ff7300" name="Entries" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bap-graph-box">
              <h4>Weekly Report</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }} barSize={weeklyBarSize}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis yAxisId="left" orientation="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="people" fill="#8884d8" name="People" />
                  <Line yAxisId="right" type="monotone" dataKey="entries" stroke="#ff7300" name="Entries" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={isMobile ? 'bap-stack' : 'bap-row'}>
            <div className="bap-graph-box">
              <h4>Hourly Entries & People</h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={hourlyData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis yAxisId="left" orientation="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="people" fill="#8884d8" name="People" />
                  <Line yAxisId="right" type="monotone" dataKey="entries" stroke="#ff7300" name="Entries" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bap-graph-box">
              <h4>Waiting Number vs Avg Wait Time</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={waitingData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="waiting_number" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="avg_wait" stroke="#8884d8" name="Avg Wait (min)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ marginBottom: 32 }}>
            <h4>Deletion Reasons</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ccc', padding: 8 }}>Reason</th>
                  <th style={{ border: '1px solid #ccc', padding: 8 }}>Entries</th>
                  <th style={{ border: '1px solid #ccc', padding: 8 }}>People</th>
                </tr>
              </thead>
              <tbody>
                {deletionReasons.map((row, idx) => (
                  <tr key={row.reason || idx}>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{row.reason}</td>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{row.entries}</td>
                    <td style={{ border: '1px solid #ccc', padding: 8 }}>{row.people}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessAnalyticsPage; 